import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import CalendarIcon from '@mui/icons-material/CalendarMonth'
import ListIcon from '@mui/icons-material/ViewList'
import { sortBy } from 'ramda'
import { EventCalendar } from '@mui/x-scheduler/event-calendar'
import type { SchedulerEvent, SchedulerResource } from '@mui/x-scheduler/models'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchAllInstructorSessions,
  selectFOAMfratInstructors,
  selectInstructorSessions,
} from 'src/v3/content-pipeline/instructor.slice'
import { selectEvents } from 'src/v3/content-pipeline/event-pipeline.slice'
import axios from 'src/utilities/http-client'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import SessionFormModal from './calendar/session-form-modal'
import InstructorFilter from './instructor-filter'
import SentimentAnalysisModal from './components/sentiment-analysis-modal'
import InstructorLinkReportSection from './components/instructor-link-report'
import SessionListView from './components/session-list-view'

dayjs.extend(utc)

// Palette cycles through these for instructors without a fixed color
let INSTRUCTOR_COLORS: SchedulerEvent['color'][] = [
  'teal',
  'indigo',
  'orange',
  'pink',
  'lime',
  'purple',
  'amber',
  'blue',
  'green',
]

type EditingSession = {
  id: string
  eventID: string
  name?: string
  sessionTime: string
  endTime?: string
  timeZone: string
  instructorIDs: string[]
}

export default function AdminSchedulerView(): ReactElement {
  let dispatch = useAppDispatch()
  let instructors = useAppSelector(selectFOAMfratInstructors)
  let sessions = useAppSelector(selectInstructorSessions)
  let events = useAppSelector(selectEvents)
  let { showError, showSuccess, NotificationComponent } = useNotifications()

  let [isFormOpen, setIsFormOpen] = useState(false)
  let [editingSession, setEditingSession] = useState<EditingSession | null>(
    null,
  )
  let [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  let [deletingSessionId, setDeletingSessionId] = useState<string | null>(null)
  let [isDeleting, setIsDeleting] = useState(false)
  let [selectedInstructorIDs, setSelectedInstructorIDs] = useState<string[]>([])
  let [isSentimentOpen, setIsSentimentOpen] = useState(false)
  let [sentimentPresetInstructorID, setSentimentPresetInstructorID] = useState<
    string | undefined
  >(undefined)
  let [scheduleViewMode, setScheduleViewMode] = useState<'calendar' | 'list'>(
    'calendar',
  )
  let [presetInstructorID, setPresetInstructorID] = useState('')

  let userTimeZone = dayjs.tz.guess()

  // Assign a stable color to each instructor by index
  let instructorColorMap = useMemo(
    () =>
      new Map(
        instructors.map((i, idx) => [
          i.id,
          INSTRUCTOR_COLORS[idx % INSTRUCTOR_COLORS.length],
        ]),
      ),
    [instructors],
  )

  let resources = useMemo(
    (): SchedulerResource[] =>
      instructors.map((i) => ({
        id: i.id,
        title: i.credentials ? `${i.name} (${i.credentials})` : i.name,
        eventColor: instructorColorMap.get(i.id),
      })),
    [instructors, instructorColorMap],
  )

  // One calendar event per session; use first instructor as the resource for color assignment.
  // All instructors are shown in the title. Falls back to event-level instructors when session has none.
  let schedulerEvents = useMemo(
    (): SchedulerEvent[] =>
      sessions.map((session) => {
        let event = events.find((e) => e.id === session.eventID)
        let effectiveInstructorIDs =
          session.instructorIDs.length > 0
            ? session.instructorIDs
            : (event?.instructorIDs ?? [])
        let instructorNames = effectiveInstructorIDs
          .map((id) => instructors.find((i) => i.id === id)?.name)
          .filter(Boolean)
          .join(', ')
        let title = instructorNames
          ? `${event?.name ?? 'Session'} — ${instructorNames}`
          : (event?.name ?? 'Session')
        let end =
          session.endTime ??
          dayjs
            .utc(session.sessionTime)
            .add(event?.hours ?? 1, 'hour')
            .toISOString()

        return {
          id: session.id,
          title,
          start: session.sessionTime,
          end,
          resource: effectiveInstructorIDs[0] ?? null,
          className: `sched-ev-${session.id}`,
        }
      }),
    [sessions, events, instructors],
  )

  // Optimistic view of events while a drag/resize update is in flight —
  // avoids the calendar snapping back to the stale Redux state before the
  // PUT + refetch resolves.
  let [pendingEvents, setPendingEvents] = useState<SchedulerEvent[] | null>(
    null,
  )

  useEffect(() => {
    if (!pendingEvents) return
    let stillPending = pendingEvents.some((pending) => {
      let live = schedulerEvents.find((e) => e.id === pending.id)
      return !live || live.start !== pending.start || live.end !== pending.end
    })
    if (!stillPending) setPendingEvents(null)
  }, [schedulerEvents, pendingEvents])

  let visibleSchedulerEvents = pendingEvents ?? schedulerEvents

  let visibleResources = useMemo((): Record<string, boolean> => {
    if (selectedInstructorIDs.length === 0) return {}
    return Object.fromEntries(
      instructors.map((i) => [i.id, selectedInstructorIDs.includes(i.id)]),
    )
  }, [selectedInstructorIDs, instructors])

  let upcomingSessions = useMemo(
    () =>
      sortBy(
        (s) => dayjs.utc(s.sessionTime).valueOf(),
        sessions.filter((s) => dayjs.utc(s.sessionTime).isAfter(dayjs.utc())),
      ),
    [sessions],
  )

  // Mirrors the calendar's visibleResources filtering so the List view shows
  // the same sessions as the Calendar view for the current instructor filter.
  let filteredUpcomingSessions = useMemo(() => {
    if (selectedInstructorIDs.length === 0) return upcomingSessions
    return upcomingSessions.filter((session) => {
      let event = events.find((e) => e.id === session.eventID)
      let effectiveInstructorIDs =
        session.instructorIDs.length > 0
          ? session.instructorIDs
          : (event?.instructorIDs ?? [])
      return effectiveInstructorIDs.some((id) =>
        selectedInstructorIDs.includes(id),
      )
    })
  }, [upcomingSessions, events, selectedInstructorIDs])

  let sessionToDelete =
    upcomingSessions.find((s) => s.id === deletingSessionId) ??
    sessions.find((s) => s.id === deletingSessionId)

  let handleEdit = (session: (typeof upcomingSessions)[number]) => {
    setEditingSession({
      id: session.id,
      eventID: session.eventID,
      name: session.name,
      sessionTime: session.sessionTime,
      endTime: session.endTime,
      timeZone: session.timeZone,
      instructorIDs: session.instructorIDs,
    })
    setIsFormOpen(true)
  }

  let handleEventsChange = (value: SchedulerEvent[]) => {
    let changed = value.filter((event) => {
      let current = visibleSchedulerEvents.find((e) => e.id === event.id)
      return (
        current && (current.start !== event.start || current.end !== event.end)
      )
    })
    if (changed.length === 0) return

    setPendingEvents(value)

    changed.forEach((changedEvent) => {
      let session = sessions.find((s) => s.id === changedEvent.id)
      if (!session) return

      axios
        .put('/api/pipeline/instructor/session', {
          id: session.id,
          eventID: session.eventID,
          ...(session.name && { name: session.name }),
          sessionTime: dayjs(changedEvent.start).utc().format(),
          ...(changedEvent.end && {
            endTime: dayjs(changedEvent.end).utc().format(),
          }),
          timeZone: session.timeZone,
          instructorIDs: session.instructorIDs,
          userIDs: [],
        })
        .then(() => {
          showSuccess('Session rescheduled')
          dispatch(fetchAllInstructorSessions())
        })
        .catch((error: any) => {
          showError(
            error?.response?.data?.message ?? 'Failed to reschedule session',
          )
          setPendingEvents(null)
        })
    })
  }

  // The calendar library has no public onEventClick — intercept the click
  // before it reaches the library's own edit dialog (capture phase runs
  // first, and nothing in the library calls stopPropagation) and open our
  // own SessionFormModal instead, keyed off the per-event className.
  let handleCalendarClickCapture = (
    event: React.MouseEvent<HTMLDivElement>,
  ) => {
    let target = event.target as HTMLElement
    let eventEl = target.closest('[class*="sched-ev-"]')
    if (!eventEl) return

    let match = Array.from(eventEl.classList).find((c) =>
      c.startsWith('sched-ev-'),
    )
    let session = sessions.find((s) => `sched-ev-${s.id}` === match)
    if (!session) return

    event.preventDefault()
    event.stopPropagation()
    handleEdit(session)
  }

  let handleScheduleForInstructor = (instructorID: string) => {
    setEditingSession(null)
    setPresetInstructorID(instructorID)
    setIsFormOpen(true)
  }

  let handleSentimentForInstructor = (instructorID: string) => {
    setSentimentPresetInstructorID(instructorID)
    setIsSentimentOpen(true)
  }

  let handleDeleteClick = (sessionID: string) => {
    setDeletingSessionId(sessionID)
    setDeleteConfirmOpen(true)
  }

  let handleDeleteConfirm = async () => {
    if (!deletingSessionId) return
    setIsDeleting(true)
    try {
      await axios.delete(
        `/api/pipeline/instructor/session/${deletingSessionId}`,
      )
      showSuccess('Session deleted')
      dispatch(fetchAllInstructorSessions())
      setDeleteConfirmOpen(false)
      setDeletingSessionId(null)
    } catch (error: any) {
      showError(error?.response?.data?.message ?? 'Failed to delete session')
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          gap: 2,
        }}
      >
        <InstructorFilter
          instructors={instructors}
          selectedIDs={selectedInstructorIDs}
          onChange={setSelectedInstructorIDs}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ToggleButtonGroup
            size="small"
            value={scheduleViewMode}
            exclusive
            onChange={(_, value) => value && setScheduleViewMode(value)}
          >
            <ToggleButton value="calendar" sx={{ px: 1, width: 115 }}>
              <CalendarIcon sx={{ mr: 0.5, height: 18, width: 18 }} />
              <span>Calendar</span>
            </ToggleButton>
            <ToggleButton value="list" sx={{ px: 1, width: 115 }}>
              <ListIcon sx={{ mr: 0.5, height: 18, width: 18 }} />
              <span>List</span>
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingSession(null)
              setPresetInstructorID('')
              setIsFormOpen(true)
            }}
          >
            Schedule Session
          </Button>
        </Box>
      </Box>

      {scheduleViewMode === 'calendar' ? (
        <Box
          onClickCapture={handleCalendarClickCapture}
          sx={{
            height: 600,
            '& .MuiEventCalendar-eventDialogPaper': {
              bgcolor: 'background.default',
            },
          }}
        >
          <EventCalendar
            events={visibleSchedulerEvents}
            resources={resources}
            visibleResources={visibleResources}
            onEventsChange={handleEventsChange}
            defaultVisibleDate={new Date()}
            defaultView="month"
            views={['month', 'week', 'day', 'agenda']}
            eventCreation={false}
          />
        </Box>
      ) : (
        <SessionListView
          sessions={filteredUpcomingSessions}
          events={events}
          instructors={instructors}
          userTimeZone={userTimeZone}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
        />
      )}

      <Card
        variant="outlined"
        sx={{
          mt: 4,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Sentiment analysis
            </Typography>
            <Chip label="Admin only" size="small" variant="outlined" />
          </Box>
          <Typography color="text.secondary" variant="body2">
            Generate an AI-powered report from learner survey responses for any
            instructor's events or courses.
            <br />
            MOTHER draws from the last 6 months of SurveyV2 data — results are
            not stored.
          </Typography>
        </Box>
        <Button
          variant="contained"
          onClick={() => {
            setSentimentPresetInstructorID(undefined)
            setIsSentimentOpen(true)
          }}
          sx={{ flexShrink: 0 }}
        >
          Run analysis
        </Button>
      </Card>

      <InstructorLinkReportSection
        onSchedule={handleScheduleForInstructor}
        onSentiment={handleSentimentForInstructor}
      />

      <Dialog
        open={deleteConfirmOpen}
        onClose={isDeleting ? undefined : () => setDeleteConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="admin-delete-session-title"
        aria-describedby="admin-delete-session-description"
      >
        <DialogTitle id="admin-delete-session-title">
          Delete Session?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="admin-delete-session-description">
            Are you sure you want to delete the session for{' '}
            <strong>
              {events.find((e) => e.id === sessionToDelete?.eventID)?.name ??
                'this event'}
            </strong>
            {sessionToDelete?.name && ` — ${sessionToDelete.name}`}? This cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
            startIcon={<DeleteIcon />}
            autoFocus
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <SessionFormModal
        open={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          setEditingSession(null)
          setPresetInstructorID('')
        }}
        instructorID={presetInstructorID}
        editSession={editingSession}
        isComplianceAdmin
        onDelete={(sessionID) => {
          setIsFormOpen(false)
          setEditingSession(null)
          setPresetInstructorID('')
          handleDeleteClick(sessionID)
        }}
      />

      <SentimentAnalysisModal
        open={isSentimentOpen}
        onClose={() => setIsSentimentOpen(false)}
        initialInstructorID={sentimentPresetInstructorID}
      />

      {NotificationComponent}
    </>
  )
}
